import { useState } from 'react';
import type { FC } from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import { Button, Dropdown } from 'antd';
import { TbFileTypeJpg, TbFileTypePng, TbFileTypeSvg, TbPhotoDown } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { toast } from '@/shared/components/toasts/PlatformToast';

export type ChartImageFormat = 'jpeg' | 'png' | 'svg';

interface ChartImageDownloadDropdownProps {
    onDownload: (format: ChartImageFormat) => Promise<void> | void;
}

const ChartImageDownloadDropdown: FC<ChartImageDownloadDropdownProps> = ({ onDownload }) => {
    const { token } = useAntDesignTheme();
    const [downloading, setDownloading] = useState(false);
    const download = async (format: ChartImageFormat) => {
        if (downloading) {
            return;
        }

        const formatLabel = format === 'jpeg' ? 'JPEG' : format.toLocaleUpperCase();
        const toastId = toast.loading(`Preparing ${formatLabel} chart image`, {
            description: 'Formatting the chart and series key for download.',
            icon: <LoadingOutlined spin />,
        });

        try {
            setDownloading(true);
            await onDownload(format);
            toast.success(`${formatLabel} Chart Download Started`, {
                description: 'The image should appear in your downloads shortly.',
                id: toastId,
            });
        } catch {
            toast.error('Chart Image Download Failed', {
                description: 'Please try again. If the issue persists, contact support.',
                id: toastId,
            });
        } finally {
            setDownloading(false);
        }
    };

    return (
        <Dropdown
            popupRender={() => (
                <DropdownMenuPanel
                    {...{
                        items: [
                            { disabled: downloading, icon: <TbFileTypePng />, key: 'png', label: 'PNG image' },
                            { disabled: downloading, icon: <TbFileTypeJpg />, key: 'jpeg', label: 'JPEG image' },
                            { disabled: downloading, icon: <TbFileTypeSvg />, key: 'svg', label: 'SVG image' },
                        ],
                        onClick: ({ key }) => void download(key as ChartImageFormat),
                    }}
                />
            )}
            placement='bottomRight'
            trigger={['click']}
        >
            <Button
                aria-label='Download chart image'
                icon={<TbPhotoDown style={{ color: token.colorTextTertiary, verticalAlign: '-0.1rem' }} />}
                loading={downloading}
                size='small'
                type='text'
            />
        </Dropdown>
    );
};

export default ChartImageDownloadDropdown;
